import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";
import { API, BRAND } from "../lib/config";
import { BrandMark } from "../components/BrandLogo";
import { SiteFooter } from "../components/SiteFooter";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { RefreshCw, Inbox, Eye, Lock, LogOut } from "lucide-react";

const ADMIN_KEY_STORAGE = "eo_admin_key";

export default function InternalSubmissions() {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(ADMIN_KEY_STORAGE) || "");
  const [keyInput, setKeyInput] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [intents, setIntents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const logout = () => {
    sessionStorage.removeItem(ADMIN_KEY_STORAGE);
    setAdminKey("");
    setSubmissions([]);
    setIntents([]);
  };

  const load = async (key = adminKey) => {
    if (!key) return;
    setLoading(true);
    try {
      const headers = { "X-Admin-Key": key };
      const [subsRes, intentsRes] = await Promise.all([
        axios.get(`${API}/onboarding`, { headers }),
        axios.get(`${API}/deposit-intents`, { headers }),
      ]);
      setSubmissions(subsRes.data);
      setIntents(intentsRes.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        toast.error("Session expired. Please sign in again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError("");
    setAuthLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { key: keyInput });
      sessionStorage.setItem(ADMIN_KEY_STORAGE, keyInput);
      setAdminKey(keyInput);
      setKeyInput("");
      load(keyInput);
    } catch (err) {
      setAuthError("Invalid admin key. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    if (adminKey) load(adminKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmt = (iso) => new Date(iso).toLocaleString("en-SG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  // ---------- Login gate ----------
  if (!adminKey) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="sticky top-0 z-50 bg-background border-b border-border">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" aria-label="EnquiryOps home">
              <BrandMark size={32} />
              <span className="font-heading font-semibold text-lg text-foreground">{BRAND.name}</span>
              <Badge variant="secondary" className="ml-1">Internal</Badge>
            </Link>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <Card className="rounded-2xl border-border shadow-[var(--shadow-md)] w-full max-w-sm">
            <CardContent className="p-6 sm:p-8">
              <span className="h-12 w-12 rounded-full bg-[var(--surface-3)] flex items-center justify-center mb-4" aria-hidden="true">
                <Lock className="h-5 w-5 text-[var(--ink)]" />
              </span>
              <h1 className="text-xl font-semibold text-foreground">Admin sign in</h1>
              <p className="text-sm text-muted-foreground mt-1 mb-5">This area contains submitted centre details and is restricted.</p>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="admin-key">Admin key</Label>
                  <Input
                    id="admin-key"
                    type="password"
                    value={keyInput}
                    onChange={(e) => setKeyInput(e.target.value)}
                    placeholder="Enter admin key"
                    autoComplete="current-password"
                    required
                    aria-invalid={!!authError}
                    data-testid="admin-key-input"
                  />
                  {authError && (
                    <p className="text-xs font-medium text-foreground" role="alert" data-testid="admin-login-error">{authError}</p>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={authLoading || !keyInput}
                  className="w-full rounded-xl bg-[var(--brand)] text-[var(--paper)] hover:bg-[var(--brand-strong)] focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2"
                  data-testid="admin-login-button"
                >
                  {authLoading ? "Checking..." : "Sign in"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-background border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <BrandMark size={32} />
            <span className="font-heading font-semibold text-lg text-foreground">{BRAND.name}</span>
            <Badge variant="secondary" className="ml-1">Internal</Badge>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="secondary" className="rounded-xl border border-border" onClick={() => load()} data-testid="submissions-refresh-button">
              <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
            </Button>
            <Button variant="ghost" className="rounded-xl" onClick={logout} data-testid="admin-logout-button">
              <LogOut className="h-4 w-4 mr-1.5" /> Log out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">Onboarding submissions</h1>
          <p className="text-muted-foreground mt-1 text-sm">Centres that placed a deposit and completed the onboarding form.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {[
            { label: "Onboarding submissions", value: submissions.length },
            { label: "Deposit CTA clicks", value: intents.length },
            { label: "Latest submission", value: submissions[0] ? fmt(submissions[0].created_at) : "—" },
          ].map((k) => (
            <Card key={k.label} className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
              <CardContent className="p-5">
                <p className="text-xs text-muted-foreground font-medium">{k.label}</p>
                <p className="text-2xl font-heading font-semibold tabular-nums text-foreground mt-1">{k.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-lg" />)}
              </div>
            ) : submissions.length === 0 ? (
              <div className="p-12 text-center" data-testid="submissions-empty-state">
                <Inbox className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="font-semibold text-foreground">No submissions yet</p>
                <p className="text-sm text-muted-foreground mt-1">When a centre completes the onboarding form on /thank-you, it appears here.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="submissions-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Centre</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead className="hidden md:table-cell">Branches</TableHead>
                      <TableHead className="hidden md:table-cell">Monthly enquiries</TableHead>
                      <TableHead className="hidden lg:table-cell">Sources</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {submissions.map((s) => (
                      <TableRow key={s.id} className="hover:bg-secondary">
                        <TableCell className="font-medium">{s.centre_name}</TableCell>
                        <TableCell>
                          <p className="text-sm">{s.contact_name}</p>
                          <p className="text-xs text-muted-foreground">{s.contact_email}</p>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm">{s.branches || "—"}</TableCell>
                        <TableCell className="hidden md:table-cell text-sm">{s.monthly_enquiries || "—"}</TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <div className="flex flex-wrap gap-1 max-w-[220px]">
                            {(s.enquiry_sources || []).slice(0, 3).map((src) => (
                              <Badge key={src} variant="secondary" className="text-[10px]">{src}</Badge>
                            ))}
                            {(s.enquiry_sources || []).length > 3 && <Badge variant="secondary" className="text-[10px]">+{s.enquiry_sources.length - 3}</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmt(s.created_at)}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" className="rounded-lg" onClick={() => setSelected(s)} data-testid={`submission-view-${s.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      <SiteFooter />

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">{selected?.centre_name}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              {[
                ["Contact", `${selected.contact_name} · ${selected.contact_email}${selected.contact_phone ? ` · ${selected.contact_phone}` : ""}`],
                ["Branches", selected.branches || "—"],
                ["Programmes", selected.programmes || "—"],
                ["Monthly enquiries", selected.monthly_enquiries || "—"],
                ["Enquiry sources", (selected.enquiry_sources || []).join(", ") || "—"],
                ["Current booking / follow-up process", selected.booking_process || "—"],
                ["Biggest conversion leak", selected.biggest_leak || "—"],
                ["Submitted", fmt(selected.created_at)],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
                  <p className="text-foreground mt-0.5 leading-relaxed">{value}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
